// CreateSubmission - Form to create a new expense submission
// General: Asks for period + notes
// Custom Programs: Auto-creates and goes to receipt entry

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import CreateSubmissionButton from '../common/CreateSubmissionButton';
import { useToast } from '../../contexts/ToastContext';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { submissionsAPI, programsAPI } from '../../services/api';
import dayjs from 'dayjs';

const CreateSubmission: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();

  const programIdFromState = (location.state as any)?.programId;

  // State
  const [program, setProgram] = useState<any>(null);
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [month, setMonth] = useState<number>(dayjs().month() + 1);
  const [year, setYear] = useState<number>(dayjs().year());
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (programIdFromState) {
      fetchProgram(programIdFromState);
    } else {
      setError('No program selected');
      setLoadingProgram(false);
    }
  }, [programIdFromState]);

  const fetchProgram = async (programId: string) => {
    try {
      setLoadingProgram(true);
      const data = await programsAPI.getProgram(programId);
      setProgram(data);
      
      // If it's a custom program (not General), auto-create submission
      if (!data.isGeneral) {
        await createCustomProgramSubmission(data);
      } else {
        setLoadingProgram(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load program');
      setLoadingProgram(false);
      console.error('Error fetching program:', err);
    }
  };

  const createCustomProgramSubmission = async (programData: any) => {
    try {
      setSubmitting(true);
      
      const newSubmission = await submissionsAPI.createSubmission({
        programId: programData.programId,
        periodMonth: null, // Not needed for custom programs
        periodYear: null,
        submissionNotes: `${programData.programName} submission`, // Auto-generated
      });

      showSuccess('Submission created! Add your receipts.');
      navigate(`/submissions/${newSubmission.submissionId}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to create submission';
      showError(errorMessage);
      setError(errorMessage);
      setSubmitting(false);
      setLoadingProgram(false);
    }
  };

  const generatePeriodOptions = () => {
    const options: { month: number; year: number }[] = [];
    const now = dayjs();
    
    for (let i = 0; i < 18; i++) {
      const date = now.subtract(i, 'month');
      options.push({
        month: date.month() + 1,
        year: date.year(),
      });
    }
    
    return options;
  };

  const periodOptions = generatePeriodOptions();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!notes.trim()) {
      setError('Submission notes are required');
      return;
    }

    if (!program) {
      setError('Program not loaded');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const newSubmission = await submissionsAPI.createSubmission({
        programId: program.programId,
        periodMonth: month,
        periodYear: year,
        submissionNotes: notes,
      });

      showSuccess('Submission created successfully!');
      navigate(`/submissions/${newSubmission.submissionId}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to create submission';
      showError(errorMessage);
      setError(errorMessage);
      console.error('Error creating submission:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loadingProgram || submitting) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {submitting ? 'Creating submission...' : 'Loading program...'}
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error && !program) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  // This should only render for General Expense program
  if (!program || !program.isGeneral) {
    return null; // Should have auto-redirected
  }

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom>
        Create New Submission
      </Typography>
      
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6" color="primary">
          {program.programName}
        </Typography>
        <Chip label="General Expense" size="small" color="success" />
      </Box>

      {program.description && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {program.description}
        </Typography>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Period Selection */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Expense Period
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Select the month and year for these expenses
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Month"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  required
                >
                  {monthNames.map((name, index) => (
                    <MenuItem key={index + 1} value={index + 1}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Year"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  required
                >
                  {Array.from(new Set(periodOptions.map(p => p.year))).map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Submission Notes */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Submission Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes"
                  placeholder="Describe the purpose of these expenses..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  required
                  helperText="Provide context for this expense submission (required)"
                />
              </Grid>

              {/* Expense Categories */}
              {program.expenseCategories && program.expenseCategories.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Approved Categories:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {program.expenseCategories.map((category: string) => (
                      <Chip
                        key={category}
                        label={category}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Error Display */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/')}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : 'Create Submission'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateSubmission;